import { createBrowserRouter, RouterProvider, Outlet } from 'react-router-dom'
import { ProtectedRoute } from '@components/auth/ProtectedRoute'
import { PublicOnlyRoute } from '@components/auth/PublicOnlyRoute'
import { MainLayout } from '@components/layout/MainLayout'
import { AuthLayout } from '@components/layout/AuthLayout'

// Pages
import LeaderboardPage from '@pages/LeaderboardPage'
import CollectionsPage from '@pages/CollectionsPage'
import LoginPage from '@features/auth/pages/LoginPage'
import RegisterPage from '@features/auth/pages/RegisterPage'
import MfaVerifyPage from '@features/auth/pages/MfaVerifyPage'
import ForgotPasswordPage from '@features/auth/pages/ForgotPasswordPage'
import VerifyResetCodePage from '@features/auth/pages/VerifyResetCodePage'
import ResetPasswordPage from '@features/auth/pages/ResetPasswordPage'
import { SearchResultsPage } from '@features/search/pages/SearchResultsPage'
import { AdvancedSearchPage } from '@features/search/pages/AdvancedSearchPage'
import { GroupsPage } from '@features/social/pages/GroupsPage'
import { GroupDetailsPage } from '@features/social/pages/GroupDetailsPage'
import { ShareCodePage } from '@features/social/pages/ShareCodePage'
import ProfilePage from '@features/profile/pages/ProfilePage'
import ProfileEditPage from '@features/profile/pages/ProfileEditPage'
import SettingsPage from '@features/profile/pages/SettingsPage'
import AdminDashboard from '@features/admin/pages/AdminDashboard'
import AdminAdventuresPage from '@features/admin/pages/AdminAdventuresPage'
import AdminUsersPage from '@features/admin/pages/AdminUsersPage'
import AdminUserDetailPage from '@features/admin/pages/AdminUserDetailPage'
import AdminThemesPage from '@features/admin/pages/AdminThemesPage'
import AdminBadgesPage from '@features/admin/pages/AdminBadgesPage'
import ContributionModerationPage from '@features/admin/pages/ContributionModerationPage'
import SubscriptionPage from '@features/subscriptions/pages/SubscriptionPage'
import MyRewardsPage from '@features/rewards/pages/MyRewardsPage'
import RewardDetailPage from '@features/rewards/pages/RewardDetailPage'
import GroupRewardTemplatesPage from '@features/rewards/pages/GroupRewardTemplatesPage'
import GroupRewardTemplateEditPage from '@features/rewards/pages/GroupRewardTemplateEditPage'
import GroupRewardTemplateStatsPage from '@features/rewards/pages/GroupRewardTemplateStatsPage'
import MediaManagerPage from '@features/media/pages/MediaManagerPage'
import CreateTicketPage from '@features/tickets/pages/CreateTicketPage'
import MyTicketsPage from '@features/tickets/pages/MyTicketsPage'
import TicketDetailPage from '@features/tickets/pages/TicketDetailPage'
import ModeratorTicketsPage from '@features/tickets/pages/ModeratorTicketsPage'
import ModeratorTicketDetailPage from '@features/tickets/pages/ModeratorTicketDetailPage'
import AdminTicketStatisticsPage from '@features/tickets/pages/AdminTicketStatisticsPage'
import LogsPage from '@features/logs/pages/LogsPage'
import PerformancePage from '@features/performance/pages/PerformancePage'
import CrashesPage from '@features/crashes/pages/CrashesPage'
import CrashGroupPage from '@features/crashes/pages/CrashGroupPage'
import DiagnosticsPage from '../features/diagnostics/pages/DiagnosticsPage'
import StatisticsPage from '@features/statistics/pages/StatisticsPage'
import MarketingPage from '@features/marketing/pages/MarketingPage'
import AdminToolsPage from '@features/admin/pages/AdminToolsPage'
import AdminScoringPage from '@features/admin/pages/AdminScoringPage'
import AdminNdaPage from '@features/admin/pages/AdminNdaPage'
import AdminIpBansPage from '@features/admin/pages/AdminIpBansPage'
import AdminPlansPage from '@features/admin/pages/AdminPlansPage'
import AdminNarrationPage from '@features/admin/pages/AdminNarrationPage'
import AdminStoragePage from '@features/admin/pages/AdminStoragePage'
import AdminStoriesPage from '@features/stories/pages/AdminStoriesPage'
import UnauthorizedPage from '@pages/UnauthorizedPage'
import NotFoundPage from '@pages/NotFoundPage'

// Layout wrapper components to prevent remounting on navigation
function ProtectedMainLayout() {
  return (
    <ProtectedRoute>
      <MainLayout>
        <Outlet />
      </MainLayout>
    </ProtectedRoute>
  )
}

function ProtectedAdminLayout() {
  return (
    <ProtectedRoute requiredRoles={['Admin']}>
      <MainLayout>
        <Outlet />
      </MainLayout>
    </ProtectedRoute>
  )
}

function PublicAuthLayout() {
  return (
    <PublicOnlyRoute>
      <AuthLayout>
        <Outlet />
      </AuthLayout>
    </PublicOnlyRoute>
  )
}

const router = createBrowserRouter([
  // Share code routes - public so they can handle auth redirects with referral codes
  {
    path: '/code/:shareCode',
    element: <ShareCodePage />,
  },
  {
    path: '/code',
    element: <ShareCodePage />,
  },
  // Public auth routes (login, register, etc.)
  {
    element: <PublicAuthLayout />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/register', element: <RegisterPage /> },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
      { path: '/verify-reset-code', element: <VerifyResetCodePage /> },
      { path: '/reset-password', element: <ResetPasswordPage /> },
    ],
  },
  // MFA verify (no PublicOnlyRoute - user might be partially authenticated)
  {
    path: '/mfa-verify',
    element: (
      <AuthLayout>
        <MfaVerifyPage />
      </AuthLayout>
    ),
  },
  // Admin routes (root level since this is the admin portal)
  {
    element: <ProtectedAdminLayout />,
    children: [
      { path: '/', element: <AdminDashboard /> },
      { path: '/adventures', element: <AdminAdventuresPage /> },
      { path: '/plans', element: <AdminPlansPage /> },
      { path: '/narration', element: <AdminNarrationPage /> },
      { path: '/themes', element: <AdminThemesPage /> },
      { path: '/tickets/statistics', element: <AdminTicketStatisticsPage /> },
      { path: '/statistics', element: <StatisticsPage /> },
      { path: '/marketing', element: <MarketingPage /> },
      { path: '/logs', element: <LogsPage /> },
      { path: '/performance', element: <PerformancePage /> },
      { path: '/crashes', element: <CrashesPage /> },
      { path: '/crashes/:fingerprint', element: <CrashGroupPage /> },
      // Under ProtectedAdminLayout on purpose. Registering it the way /tickets is — on the plain
      // layout with no requiredRoles — silently defaults to Admin-only, which is what locks
      // moderators out of a page their own sidebar offers them.
      { path: '/diagnostics', element: <DiagnosticsPage /> },
      { path: '/tools', element: <AdminToolsPage /> },
      { path: '/scoring', element: <AdminScoringPage /> },
      { path: '/nda', element: <AdminNdaPage /> },
      { path: '/ip-bans', element: <AdminIpBansPage /> },
      { path: '/storage', element: <AdminStoragePage /> },
      { path: '/badges', element: <AdminBadgesPage /> },
      { path: '/contributions', element: <ContributionModerationPage /> },
      { path: '/stories', element: <AdminStoriesPage /> },
      { path: '/users', element: <AdminUsersPage /> },
      { path: '/users/:userId', element: <AdminUserDetailPage /> },
    ],
  },
  // Main protected routes with MainLayout
  {
    element: <ProtectedMainLayout />,
    children: [
      { path: '/leaderboard', element: <LeaderboardPage /> },
      { path: '/collections', element: <CollectionsPage /> },
      { path: '/groups', element: <GroupsPage /> },
      { path: '/groups/:id', element: <GroupDetailsPage /> },
      { path: '/groups/:id/rewards', element: <GroupRewardTemplatesPage /> },
      { path: '/groups/:id/rewards/new', element: <GroupRewardTemplateEditPage /> },
      { path: '/groups/:id/rewards/:templateId/edit', element: <GroupRewardTemplateEditPage /> },
      { path: '/groups/:id/rewards/:templateId/stats', element: <GroupRewardTemplateStatsPage /> },
      { path: '/media', element: <MediaManagerPage /> },
      { path: '/search', element: <SearchResultsPage /> },
      { path: '/search/advanced', element: <AdvancedSearchPage /> },
      { path: '/profile', element: <ProfilePage /> },
      { path: '/profile/:userId', element: <ProfilePage /> },
      { path: '/profile/edit', element: <ProfileEditPage /> },
      { path: '/settings', element: <SettingsPage /> },
      { path: '/subscription', element: <SubscriptionPage /> },
      { path: '/rewards', element: <MyRewardsPage /> },
      { path: '/rewards/:instanceId', element: <RewardDetailPage /> },
      { path: '/support/new', element: <CreateTicketPage /> },
      { path: '/support/tickets', element: <MyTicketsPage /> },
      { path: '/support/tickets/:ticketId', element: <TicketDetailPage /> },
      { path: '/tickets', element: <ModeratorTicketsPage /> },
      { path: '/tickets/:ticketId', element: <ModeratorTicketDetailPage /> },
      { path: '/unauthorized', element: <UnauthorizedPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
])

export function AppRouter() {
  return <RouterProvider router={router} />
}
